import { useEffect } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush } from 'capacitor-sfmc-mobile-push';
import { rememberDeepLinkTarget } from './deepLinkTarget';

// Auth-flow routes are transient landing pages, never a real destination — remembering one as
// the post-login target makes AuthAccessPage redirect back to itself after a successful magic
// link (a same-URL `navigate()` that React Router treats as a no-op), leaving the page stuck on
// its final `return null` with no visible content.
function isAuthFlowPath(pathname: string): boolean {
  return pathname.startsWith('/auth-access') || pathname.startsWith('/auth-redirect');
}

/**
 * Resolves a raw URL string (custom scheme, universal/app link, or an OpenDirect deep-link
 * URL from a push notification payload) onto the in-app router. Shared by both `appUrlOpen`
 * (universal/app links, MSAL auth-redirect) and the SFMC plugin's `notificationTapped` event,
 * so a notification tap and a plain deep link both land on the same route.
 */
async function navigateToDeepLink(navigate: NavigateFunction, rawUrl: string): Promise<void> {
  // Custom URL schemes (e.g. com.jncquoi.app://) are not valid URL
  // protocol identifiers, so new URL() throws. Extract the path manually.
  let parsed: URL | null = null;
  try {
    parsed = new URL(rawUrl);
  } catch {
    const path = rawUrl.replace(/^[^:]+:\/\/[^/]*/, '') || '/';
    if (!isAuthFlowPath(path)) rememberDeepLinkTarget(path);
    navigate(path);
    return;
  }

  if (rawUrl.includes('auth-redirect')) {
    // Re-play the deep link path+search+hash onto the in-app router so that
    // handleRedirectPromise can read the auth code. MSAL's default responseMode
    // is FRAGMENT, so the code lands in parsed.hash, not parsed.search.
    // The WebView never navigated away, so MSAL's PKCE state is intact.
    await Browser.close();
    navigate(`/auth-redirect${parsed.search}${parsed.hash}`, { replace: true });
    return;
  }

  // App Links (Android) / Universal Links (iOS): the OS intercepts the HTTPS
  // URL and fires this event instead of opening the browser.
  if (parsed.pathname && parsed.pathname !== '/') {
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (!isAuthFlowPath(parsed.pathname)) rememberDeepLinkTarget(path);
    navigate(path);
  }
}

export function useDeepLink() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const appUrlListenerPromise = App.addListener('appUrlOpen', event =>
      navigateToDeepLink(navigate, event.url)
    );

    const notificationTappedListenerPromise = SfmcMobilePush.addListener('notificationTapped', payload => {
      const target = payload.customKeys?.deepLinkPath ?? payload.url;
      if (target) navigateToDeepLink(navigate, target);
    });

    return () => {
      appUrlListenerPromise.then(listener => listener.remove());
      notificationTappedListenerPromise.then(listener => listener.remove());
    };
  }, [navigate]);
}
